/** Reader state (saved / read / chosen age) for guests and for the account merge. */
export const READER_STORAGE_KEY = "dr-sara.reader.v1";

export type Engagement = { saved_at: string | null; read_at: string | null };
export type ReaderState = { v: 1; age: string | null; posts: Record<string, Engagement> };

export const emptyReader = (): ReaderState => ({ v: 1, age: null, posts: {} });

export function parseReader(raw: string | null): ReaderState {
  if (!raw) return emptyReader();
  try {
    const d = JSON.parse(raw);
    if (!d || d.v !== 1 || typeof d.posts !== "object") return emptyReader();
    const posts: Record<string, Engagement> = {};
    for (const [id, e] of Object.entries(d.posts as Record<string, Partial<Engagement>>)) {
      posts[id] = { saved_at: e?.saved_at ?? null, read_at: e?.read_at ?? null };
    }
    return { v: 1, age: typeof d.age === "string" ? d.age : null, posts };
  } catch {
    return emptyReader();
  }
}

export function loadReader(): ReaderState {
  if (typeof window === "undefined") return emptyReader();
  return parseReader(window.localStorage.getItem(READER_STORAGE_KEY));
}

export function saveReader(state: ReaderState) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(READER_STORAGE_KEY, JSON.stringify(state));
}

export function hasLocalData(s: ReaderState): boolean {
  return Object.values(s.posts).some((e) => e.saved_at || e.read_at);
}

const earliest = (a: string | null, b: string | null) => {
  if (!a) return b;
  if (!b) return a;
  return new Date(a) <= new Date(b) ? a : b;
};

export type EngagementRow = Engagement & { post_id: string };

/** Union of device and account rows, keeping the earliest timestamp of each kind. */
export function mergeEngagement(local: Record<string, Engagement>, remote: EngagementRow[]): EngagementRow[] {
  const out = new Map<string, EngagementRow>();
  for (const r of remote) out.set(r.post_id, { post_id: r.post_id, saved_at: r.saved_at, read_at: r.read_at });
  for (const [post_id, e] of Object.entries(local)) {
    const cur = out.get(post_id);
    out.set(post_id, {
      post_id,
      saved_at: earliest(cur?.saved_at ?? null, e.saved_at),
      read_at: earliest(cur?.read_at ?? null, e.read_at),
    });
  }
  return [...out.values()];
}

/** Only rows that differ from the account copy need writing back. */
export function rowsToUpsert(merged: EngagementRow[], remote: EngagementRow[]): EngagementRow[] {
  const byId = new Map(remote.map((r) => [r.post_id, r]));
  return merged.filter((m) => {
    const r = byId.get(m.post_id);
    return !r || r.saved_at !== m.saved_at || r.read_at !== m.read_at;
  });
}

/** Signed in = a real (non-anonymous) session. Booking guests stay on the device. */
export function readerMode(session: { user?: { is_anonymous?: boolean | null } | null } | null | undefined) {
  return session?.user && !session.user.is_anonymous ? "account" : "device";
}
